import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { appeler, cleNeuve } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Entete } from '../../composants/mise-en-page.tsx';
import { Carte, Erreur, Vide } from '../../composants/communs.tsx';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import type { Canal } from '../produits/types.ts';
import type { ClientEncours } from '../clients/types.ts';
import { PanierProduits } from './PanierProduits.tsx';
import { PanierTable } from './PanierTable.tsx';
import { ClientVente } from './ClientVente.tsx';
import { Encaissement } from './Encaissement.tsx';
import { verdictCredit } from './credit.ts';
import { imprimerVente } from './imprimer-vente.ts';
import { LIBELLE_CANAL as LIBELLE } from '../produits/types.ts';
import { calculerTotaux, seuilAtteint, monnaieARendre, resteAPayer } from './calculs.ts';
import { formaterFcfa } from '../../composants/ui/index.ts';
import type { DetailVente } from './types.ts';
import type { LignePanierVente, ProduitVendable, Reglement } from './vente-types.ts';

/**
 * Vente depuis le back-office — mêmes règles que le mobile (RG-002 à
 * RG-004, RG-035, RG-078), sans session de caisse : `session_caisse_id` est
 * facultatif sur `POST /ventes` (nullable côté schéma ET service), une
 * vente sans caisse ouverte est un cas prévu, pas un contournement. Ces
 * ventes restent hors du suivi « écart de caisse » — décision assumée.
 *
 * Les vérifications ci-dessous ne sont qu'un retour immédiat : le serveur
 * reste seul juge et refait tous ces contrôles (même principe que
 * `mobile/lib/vente_web/depot_vente_web.dart`, dont cet écran est le port).
 *
 * Mise en page du 04/10/2026 : produits à gauche (grille de cartes, visible
 * sans taper), client + encaissement à droite dans un panneau qui reste
 * visible sans défiler, panier en pleine largeur en dessous — la saisie
 * d'abord, le réglage ensuite.
 */
export function NouvelleVente(): ReactNode {
  const { boutiqueId: boutiqueContexte, boutiques } = useSession();
  const { ouvrir } = useApercuPdf();

  const [boutiqueId, setBoutiqueId] = useState(boutiqueContexte ?? '');
  const [canal, setCanal] = useState<Canal>('DETAIL');
  const [panier, setPanier] = useState<LignePanierVente[]>([]);
  const [remisePied, setRemisePied] = useState(0);
  const [client, setClient] = useState<ClientEncours | null>(null);
  // Nom/téléphone tapés dans le champ client, pas encore un vrai client :
  // `ClientVente` ne crée plus rien elle-même — voir la mutation plus bas,
  // qui seule décide de créer la fiche, et seulement si la vente aboutit.
  const [saisieClient, setSaisieClient] = useState<{ nom: string; telephone: string } | null>(null);
  const [reglements, setReglements] = useState<Reglement[]>([]);
  const [aCredit, setACredit] = useState(false);
  // Le navigateur n'a pas d'appareil photo intégré au flux comme le mobile :
  // la preuve de vente (RG-168) passe par un fichier choisi/déposé, pas une
  // capture. `photo_attendue` déclare l'intention au serveur ; le fichier
  // lui-même part APRÈS, sur la vente déjà créée — même route que le mobile.
  const [photoFichier, setPhotoFichier] = useState<File | null>(null);

  const totaux = calculerTotaux(panier, remisePied);
  const encaisse = reglements.reduce((somme, reglement) => somme + reglement.montant, 0);
  const manque = resteAPayer(totaux.total, reglements.map((r) => r.montant));
  const rendu = monnaieARendre(totaux.total, encaisse);

  const quantitesPanier = Object.fromEntries(
    panier.map((ligne) => [ligne.produitId, ligne.quantite]),
  );

  const mutation = useMutation({
    mutationFn: async () => {
      if (panier.length === 0) {
        throw new Error('Le panier est vide.');
      }

      const enSousSeuil = panier.find((ligne) => !seuilAtteint(ligne.quantite, ligne.quantiteMinimale));
      if (enSousSeuil !== undefined) {
        throw new Error(
          `Le seuil du canal « ${LIBELLE[canal]} » n'est pas atteint pour ${enSousSeuil.designation}.`,
        );
      }

      if (manque > 0) {
        if (!aCredit) {
          throw new Error(
            `Il manque ${formaterFcfa(manque)}. Cochez « vente à crédit », ou complétez le règlement.`,
          );
        }
        if (client === null && saisieClient === null) {
          throw new Error(
            'Une vente à crédit exige un client identifié. « Divers » ne peut pas porter de créance.',
          );
        }
        // Un client tout juste tapé n'existe pas encore : son plafond réel
        // (le défaut, à la création) ne peut se vérifier qu'au serveur, qui
        // reste de toute façon seul juge en dernier ressort.
        const refus = client === null ? null : verdictCredit(client, manque);
        if (refus !== null) {
          throw new Error(refus);
        }
      }

      if (manque < 0 && reglements.some((reglement) => reglement.mode !== 'ESPECES')) {
        throw new Error('Un règlement autre qu’espèces ne peut pas dépasser le total.');
      }

      // Le client tapé sans correspondance n'est créé QU'ICI, juste avant la
      // vente qui le référence — jamais pendant la saisie (ERG : taper un
      // nom sans aller au bout ne doit laisser aucune fiche derrière soi).
      let clientId = client?.id ?? null;
      if (clientId === null && saisieClient !== null) {
        const creeClient = await appeler<{ id: string }>('/clients', {
          methode: 'POST',
          cleIdempotence: cleNeuve(),
          corps: {
            id: cleNeuve(),
            nom: saisieClient.nom,
            ...(saisieClient.telephone !== '' ? { telephone: saisieClient.telephone } : {}),
          },
        });
        clientId = creeClient.id;
      }

      const cree = await appeler<{ id: string }>('/ventes', {
        methode: 'POST',
        cleIdempotence: cleNeuve(),
        corps: {
          id: cleNeuve(),
          boutique_id: boutiqueId,
          client_id: clientId,
          canal,
          photo_attendue: photoFichier !== null,
          lignes: panier.map((ligne) => ({
            produit_id: ligne.produitId,
            quantite: ligne.quantite,
            ...(ligne.remiseLigne > 0 ? { remise_ligne: ligne.remiseLigne } : {}),
            // Absent si inchangé : le serveur applique alors le prix
            // catalogue, comme avant cette fonctionnalité.
            ...(ligne.prixUnitaire !== ligne.prixCatalogue
              ? { prix_unitaire: ligne.prixUnitaire }
              : {}),
            ...(() => {
              const paires = ligne.imeis
                .map((paire) => ({ imei: paire.imei.trim(), imei2: paire.imei2.trim() }))
                .filter((paire) => paire.imei !== '' || paire.imei2 !== '')
                .map((paire) => ({
                  ...(paire.imei !== '' ? { imei: paire.imei } : {}),
                  ...(paire.imei2 !== '' ? { imei2: paire.imei2 } : {}),
                }));
              return paires.length > 0 ? { imeis: paires } : {};
            })(),
          })),
          ...(remisePied > 0 ? { remise_pied: remisePied } : {}),
          paiements: reglements.map((reglement) => ({ mode: reglement.mode, montant: reglement.montant })),
        },
      });

      // Route SÉPARÉE de la vente, à dessein : la vente est déjà acquise
      // quand la photo part, un envoi de fichier qui échoue ne doit jamais
      // remettre en cause un encaissement déjà enregistré.
      if (photoFichier !== null) {
        const formulaire = new FormData();
        formulaire.append('photo', photoFichier);
        await appeler(`/ventes/${cree.id}/photos`, { methode: 'POST', formulaire });
      }

      return await appeler<DetailVente>(`/ventes/${cree.id}`);
    },
    onSuccess: (detail) => {
      ouvrir(() => imprimerVente(detail));
      setPanier([]);
      setRemisePied(0);
      setClient(null);
      setSaisieClient(null);
      setReglements([]);
      setACredit(false);
      setPhotoFichier(null);
    },
  });

  function ajouterProduit(produit: ProduitVendable): void {
    setPanier((actuel) => {
      const existante = actuel.find((ligne) => ligne.produitId === produit.id);
      if (existante !== undefined) {
        return actuel.map((ligne) =>
          ligne.produitId === produit.id ? { ...ligne, quantite: ligne.quantite + 1 } : ligne,
        );
      }
      return [
        ...actuel,
        {
          produitId: produit.id,
          designation: produit.designation,
          quantite: Math.max(1, produit.quantiteMinimale),
          prixUnitaire: produit.prixUnitaire,
          prixCatalogue: produit.prixUnitaire,
          remiseLigne: 0,
          quantiteMinimale: produit.quantiteMinimale,
          imeis: [],
        },
      ];
    });
  }

  const boutiqueChoisie = boutiques.find((b) => b.id === boutiqueId);

  return (
    <>
      <Entete titre="Nouvelle vente" sous="Panier, client et encaissement — comme au comptoir." />

      <Erreur erreur={mutation.error} />

      <div className="pile">
        {boutiqueContexte === null && (
          <Carte titre="Boutique">
            <div className="champ" style={{ margin: 0, maxWidth: 320 }}>
              <select
                required
                value={boutiqueId}
                onChange={(evenement) => {
                  setBoutiqueId(evenement.target.value);
                }}
              >
                <option value="">Sélectionner…</option>
                {boutiques.map((boutique) => (
                  <option key={boutique.id} value={boutique.id}>
                    {boutique.code} — {boutique.nom}
                  </option>
                ))}
              </select>
            </div>
            {boutiqueChoisie !== undefined && (
              <p className="entete__sous" style={{ marginTop: 8 }}>
                Vente pour {boutiqueChoisie.code} — {boutiqueChoisie.nom}.
              </p>
            )}
          </Carte>
        )}

        {boutiqueId === '' ? (
          <Carte>
            <Vide texte="Choisissez une boutique pour commencer une vente." />
          </Carte>
        ) : (
          <div className="vente-mise-en-page">
            <div className="pile">
              <PanierProduits
                boutiqueId={boutiqueId}
                canal={canal}
                onCanal={setCanal}
                onAjouter={ajouterProduit}
                quantitesPanier={quantitesPanier}
              />

              <PanierTable
                panier={panier}
                canal={canal}
                remisePied={remisePied}
                onModifierLigne={(produitId, champs) => {
                  setPanier((actuel) =>
                    actuel.map((ligne) => {
                      if (ligne.produitId !== produitId) return ligne;
                      const fusionnee = { ...ligne, ...champs };
                      // Une quantité réduite ne peut pas garder plus d'IMEI que d'unités.
                      if (fusionnee.imeis.length > fusionnee.quantite) {
                        fusionnee.imeis = fusionnee.imeis.slice(0, fusionnee.quantite);
                      }
                      return fusionnee;
                    }),
                  );
                }}
                onRetirerLigne={(produitId) => {
                  setPanier((actuel) => actuel.filter((ligne) => ligne.produitId !== produitId));
                }}
                onRemisePied={setRemisePied}
                onVider={() => {
                  setPanier([]);
                  setRemisePied(0);
                }}
              />
            </div>

            <div className="vente-panneau pile">
              <ClientVente
                client={client}
                onChoisir={setClient}
                onRetirer={() => {
                  setClient(null);
                }}
                onSaisie={setSaisieClient}
              />

              <Encaissement
                reglements={reglements}
                onAjouter={(reglement) => {
                  setReglements((actuel) => [...actuel, reglement]);
                }}
                onRetirer={(index) => {
                  setReglements((actuel) => actuel.filter((_, i) => i !== index));
                }}
                manque={manque}
                rendu={rendu}
                encaisse={encaisse}
                total={totaux.total}
                aCredit={aCredit}
                onACredit={setACredit}
                panierVide={panier.length === 0}
                enregistrement={mutation.isPending}
                onEncaisser={() => {
                  mutation.mutate();
                }}
                photoFichier={photoFichier}
                onPhotoFichier={setPhotoFichier}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
